import { useRouter } from "next/router"
import { useState, useCallback, useEffect } from "react"
import { useForm } from "react-hook-form"
import { FieldErrors, FieldValues } from "react-hook-form"

import { fetchPostAuthRegister } from "~/api/auth/register/post"
import { internalLink } from "~/services/helper"
import { useStores } from "~/stores/StoreProvider"

import { DATA_LAYER_EVENT } from "../InputProfileFlowPages/consts/gtm"
import type { AuthRegisterPageProps } from "./index"
import { HackedPostAuthRegisterBody } from "./types"
import { useAuthRegisterRouterQuery } from "./useAuthRegisterRouterQuery"

export const useAuthRegisterPage = ({ onSubmitForMock }: AuthRegisterPageProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isRecaptchaAuth, setIsRecaptchaAuth] = useState<string | null>(null)
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>()

  const { userStore } = useStores()
  const router = useRouter()
  const query = useAuthRegisterRouterQuery()

  const { handleSubmit, control } = useForm<HackedPostAuthRegisterBody>()

  useEffect(() => {
    if (!userStore.requestedFetchGetMe) {
      return
    }
    setIsLoggedIn(userStore.authed)
  }, [userStore.requestedFetchGetMe])

  const onBeforeSubmit = useCallback(() => {
    setErrorMessages([])
  }, [])

  /**
   * handleSubmitでinValidになった項目のエラーメッセージをまとめて登録する
   */
  const handleSubmitError = (errors: FieldErrors<FieldValues>) => {
    const messages = Object.entries(errors)
      .flatMap(([, fieldError]): Array<string | undefined> => {
        if (Array.isArray(fieldError)) {
          return fieldError.map(({ message }) => message)
        } else {
          return [fieldError?.message]
        }
      })
      .filter((it): it is string => it != null)
    setErrorMessages(messages)
  }

  /**
   * 新規登録Step1(必須項目)送信処理
   */
  const onSubmit = handleSubmit(async (body) => {
    setIsLoading(true)

    if (query.nonce) {
      body.nonce = query.nonce
    }

    if (query.redirect_after_auth) {
      body.redirect_after_auth = query.redirect_after_auth
    }

    if (query.utm_source || query.utm_medium || query.utm_campaign || query.gad_source) {
      body.utm_source = query.utm_source
      body.utm_medium = query.utm_medium
      body.utm_campaign = query.utm_campaign
      body.gad_source = query.gad_source
    }

    // storybook only
    if (onSubmitForMock) {
      onSubmitForMock(body)
      setIsLoading(false)
      return
    }

    // local では recaptcha の検証不要なので値を送らないことでバリデーションを回避
    if (isRecaptchaAuth && process.env.ENV !== "local") {
      body.g_recaptcha_response = isRecaptchaAuth
    }

    const response = await fetchPostAuthRegister(body)

    if (response.result === "FAILED") {
      const messages = response.error.map(({ message }) => message)
      setErrorMessages(messages)
      window.scrollTo({ top: 0, behavior: "smooth" })
      setIsLoading(false)
      return
    }

    window.dataLayer.push({ event: DATA_LAYER_EVENT.COMPLETE_NEW_REGISTRATION })

    // NOTE: 紹介コード入力時はGA4にイベントを送信
    if (body.introduction_key) {
      window.dataLayer.push({ event: "input_introCode_registration" })
    }

    await userStore.fetchLoginUser()
    // NOTE: login_provider_errorは完了ページに送らない
    const { login_provider_error: _, ...q } = query
    router.replace({
      pathname: internalLink("/auth/register/complete"),
      query: q as Record<string, string>,
    })
  }, handleSubmitError)

  return {
    control,
    errorMessages,
    isLoading,
    isRecaptchaAuth,
    onBeforeSubmit,
    onSubmit,
    query,
    setIsRecaptchaAuth,
    isLoggedIn,
  }
}
